import { inject, provide } from 'vue';
import { Appointment, LeadId } from '@keyvue/one-api-client';
import { EventLogAPI, type EventLog } from '@keyvue/one-api-client-legacy';
import useClient, { type ReactiveCollection } from '@composables/useClient';
import useEventBus from '@composables/useEventBus';
import useTaskOverview, { type TaskOverview } from '@composables/useTaskOverview';
import createSharedLoad from '@/utils/createSharedLoad';

export interface LeadPageData {
    leadId: LeadId;
    appointmentCollection: () => Promise<ReactiveCollection<Appointment>>;
    taskOverview: () => Promise<TaskOverview>;
    eventLogs: () => Promise<EventLog[]>;
}

/**
 * A lead's appointments, tasks (see useTaskOverview) and event log: the data several cards in the lead
 * side sheet need.
 *
 * Each item is loaded when a card first asks for it and shared from then on. Appointments and tasks
 * are reloaded once, for every card, after their 'resource-manipulated' event; the event log is not
 * reloaded, as before.
 */
function createLeadPageData(leadId: LeadId): LeadPageData {
    const { getReactiveCollection } = useClient();
    const { getTaskOverview } = useTaskOverview();
    const eventBus = useEventBus();

    const appointmentCollection = createSharedLoad(
        () => getReactiveCollection(Appointment, { lead: leadId }),
        (current) => current.load(),
    );
    const taskOverview = createSharedLoad(
        () => getTaskOverview({ lead: leadId }),
        (current) => current.load(),
    );
    const eventLogs = createSharedLoad(() => EventLogAPI.getAll({ referral: leadId }));

    eventBus.subscribe<string>('resource-manipulated', async (params) => {
        if (params === 'Appointment') {
            await appointmentCollection.reload();
        }

        if (params === 'Task') {
            await taskOverview.reload();
        }
    });

    return {
        leadId,
        appointmentCollection: appointmentCollection.get,
        taskOverview: taskOverview.get,
        eventLogs: eventLogs.get,
    };
}

/**
 * Shares the lead's data with every card below the calling component (LeadDetail). Without it,
 * LeadProgressCard and LeadCardTabs each loaded the appointments and tasks, and LeadProgressCard and
 * LeadDetailRight each loaded the event log.
 */
export function provideLeadPageData(leadId: LeadId): LeadPageData {
    const data = createLeadPageData(leadId);
    provide('leadPageData', data);

    return data;
}

/**
 * The lead side sheet's shared data for this lead. Outside the sheet, or for another lead, the card
 * gets its own copy, loaded and reloaded the same way.
 */
export default function useLeadPageData(leadId: LeadId): LeadPageData {
    const provided = inject<LeadPageData | null>('leadPageData', null);

    if (provided && provided.leadId.toString().toLowerCase() === leadId.toString().toLowerCase()) {
        return provided;
    }

    return createLeadPageData(leadId);
}
